import { Link } from "react-router-dom";
import { Briefcase, MapPin, Sparkles, Clock3, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import type { JobDetail } from "../../api/jobs";
import { isEmployerVerified } from "../../lib/jobVerification";

type FeaturedJobsProps = {
  jobs: JobDetail[];
  variant?: "grid" | "carousel";
};

const statusCopy: Record<string, string> = {
  active: "Open",
  closed: "Closed",
  flagged: "Flagged",
};

export function FeaturedJobs({ jobs, variant = "grid" }: FeaturedJobsProps) {
  if (!jobs.length) {
    return null;
  }

  return (
    <div
      className={variant === "grid" ? "grid gap-6 sm:grid-cols-2 xl:grid-cols-3" : "flex gap-4 overflow-x-auto pb-2"}
      role="list"
      aria-label="Featured job opportunities"
    >
      {jobs.map((job) => {
        const verified = isEmployerVerified(job);
        return (
        <Card
          key={job.id}
          role="listitem"
          className="group relative border-white/10 bg-white/[0.04] transition-shadow hover:border-[#d2ac47]/40 hover:shadow-[0_20px_60px_-35px_rgba(234,179,8,0.65)]"
        >
          <CardHeader className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg text-white">{job.title}</CardTitle>
              {job.urgent ? (
                <Badge className="bg-red-500/20 text-red-300 border-red-500/70">Urgent</Badge>
              ) : job.returnerFriendly ? (
                <Badge className="bg-[#ba9036]/20 text-[#f7ef8a] border-[#d2ac47]/70">Returner friendly</Badge>
              ) : job.remote ? (
                <Badge className="bg-blue-500/20 text-blue-200 border-blue-500/60">Remote</Badge>
              ) : null}
            </div>

            <p className="text-sm text-gray-300/80 line-clamp-2">{job.description}</p>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Briefcase className="h-4 w-4 text-[#ba9036]/80" aria-hidden />
                {statusCopy[job.status]}
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <MapPin className="h-4 w-4 text-[#ba9036]/80" aria-hidden />
                {job.location}
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Clock3 className="h-4 w-4 text-[#ba9036]/80" aria-hidden />
                Posted {job.posted}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-white/20 text-white/90">
                {job.type}
              </Badge>
              {job.returnerFriendly && (
                <Badge variant="outline" className="border-emerald-400/40 text-emerald-200">
                  Returners
                </Badge>
              )}
              {verified && (
                <Badge variant="outline" className="border-blue-400/40 text-blue-200 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" aria-hidden />
                  Verified
                </Badge>
              )}
            </div>

            <div className="flex flex-col gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-3 text-sm">
              <div className="flex items-center justify-between text-gray-300/90">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#ba9036]/80" aria-hidden />
                  Smart match score
                </span>
                <span className="font-semibold text-white">
                  {job.matchScore ? `${Math.round(job.matchScore)}%` : "Calibrating"}
                </span>
              </div>
              <div className="flex items-center justify-between text-gray-400">
                <span>{job.applicants} candidates</span>
                <span>{job.views} views</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-400">Salary</span>
              <span className="text-lg font-semibold text-white">{job.salary}</span>
            </div>

            <div className="flex items-center gap-3">
              <Button asChild className="flex-1 bg-gold-gradient text-black">
                <Link to={`/jobs/${(job as { slug?: string }).slug ?? job.id}`}>View role</Link>
              </Button>
              <Button variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}
